import { createContext, useContext, useCallback, useMemo, useState } from "react";
import * as v0 from "@aidkitorg/types/lib/survey";
import { type Translations } from "@aidkitorg/i18n/lib"
import { useLocalizedStrings, useLocalizer } from "../../../Localization";
import { usePost } from "../../../API";
import { type ConnectionModalState } from "./types";

type ConnectionModalDistroStrings = NonNullable<v0.DeckVerification["connectionModalText"]>;
type ConnectionModalDefaultStrings = Translations["questions"]["deck_verification"]["connection_modal"]

interface ConnectionModalContext {
  state: ConnectionModalState;
  accessToken: string | null;
  setAccessToken: (accessToken: string | null) => void;
  getLocalizedString: (
    richTextKey: keyof ConnectionModalDistroStrings,
    fallbackTextKey?: keyof ConnectionModalDefaultStrings,
  ) => string;
  onUpdateState: (newState: ConnectionModalState) => void;
  onSelectAccount: (accountId: string) => void;
  onExit: () => void;
}

const ConnectionModalContext = createContext<ConnectionModalContext | null>(null);

export function useConnectionModal() {
  const context = useContext(ConnectionModalContext)
  if (!context) {
    throw new Error("useConnectionModal must be within a ConnectionModalProvider.")
  }

  return context;
}

interface ConnectionModalProviderProps {
  children: React.ReactNode;
  component: v0.DeckVerification;
  onSubmit: (jobGuid: string) => void;
  onExit: () => void;
}

export function ConnectionModalProvider({
  children,
  component,
  onSubmit,
  onExit,
}: ConnectionModalProviderProps) {
  const L = useLocalizedStrings();
  const localizer = useLocalizer();
  const fetchBill = usePost("/deck/fetch_bill");
  const closeConnection = usePost("/deck/close_connection");

  const [state, setState] = useState<ConnectionModalState>({ stage: "search" });
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const getLocalizedString = useCallback((
    richTextKey: keyof ConnectionModalDistroStrings,
    fallbackTextKey?: keyof ConnectionModalDefaultStrings,
  ) => {
    const distroText = localizer(component.connectionModalText?.[richTextKey]);
    const defaultText = fallbackTextKey ? L.questions.deck_verification.connection_modal[fallbackTextKey] : "";
    return distroText || defaultText;
  }, [localizer, component, L]);

  const handleExit = useCallback(async () => {
    if (accessToken) {
      await closeConnection({ access_token: accessToken });
    }

    onExit();
  }, [accessToken, closeConnection, onExit]);

  const handleSelectAccount = useCallback(async (accountId: string) => {
    const jobGuid = await fetchBill({
      access_token: accessToken!,
      account_id: accountId,
      target_field: component.targetField,
    });

    onSubmit(jobGuid);
  }, [accessToken, fetchBill, component, onSubmit]);

  const contextValue = useMemo(() => ({
    state,
    accessToken,
    setAccessToken,
    getLocalizedString,
    onUpdateState: setState,
    onSelectAccount: handleSelectAccount,
    onExit: handleExit,
  }), [
    component,
    state,
    accessToken,
    getLocalizedString,
    handleSelectAccount,
    handleExit,
  ]);

  return <ConnectionModalContext.Provider value={contextValue}>
    {children}
  </ConnectionModalContext.Provider>
}
